"use client"

import { useState } from "react"
import { Form, Input, Button, Typography, Alert, Space, message, Tooltip, Switch } from "antd"
import {
  ArrowLeftOutlined,
  InfoCircleOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  CloudOutlined, // Salesforce icon
  CheckCircleOutlined,
} from "@ant-design/icons"

const { Title, Text, Link } = Typography

export default function SalesforceForm({ onBack }) {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [sandboxMode, setSandboxMode] = useState(false) // State for the sandbox switch

  const handleSubmit = async (values) => {
    setLoading(true)
    try {
      const formattedValues = {
        ...values,
        tokenExpiryDate: values.tokenExpiryDate?.toISOString() || null,
        startDate: values.startDate?.toISOString() || null,
        sandbox: sandboxMode, // Include sandbox mode in submitted values
      }
      await new Promise((resolve) => setTimeout(resolve, 1500))
      console.log("Form submitted with values:", formattedValues)
      message.success({
        content: "Salesforce source created successfully!",
        icon: <CheckCircleOutlined style={{ color: "#52c41a" }} />,
      })
      form.resetFields()
      setSandboxMode(false) // Reset sandbox mode on success
    } catch (error) {
      message.error("Failed to create source. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const handleSubmitFailed = (errorInfo) => {
    message.error("Please fix the errors in the form before submitting.")
    console.log("Form validation failed:", errorInfo)
  }

  const validateInstanceUrl = (_, value) => {
    if (!value) return Promise.resolve()
    try {
      const url = new URL(value)
      if (!url.hostname.includes("salesforce.com") && !url.hostname.includes("force.com")) {
        return Promise.reject(new Error("Instance URL must be a valid Salesforce domain"))
      }
      return Promise.resolve()
    } catch {
      return Promise.reject(new Error("Please enter a valid URL"))
    }
  }

  const validateEmail = (_, value) => {
    if (!value) return Promise.resolve()
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(value)) {
      return Promise.reject(new Error("Please enter a valid email address"))
    }
    return Promise.resolve()
  }

  const validateSecurityToken = (_, value) => {
    if (!value) return Promise.resolve()
    if (value.length !== 25) {
      return Promise.reject(new Error("Security token must be exactly 25 characters"))
    }
    if (!/^[a-zA-Z0-9]+$/.test(value)) {
      return Promise.reject(new Error("Security token can only contain letters and numbers"))
    }
    return Promise.resolve()
  }

  const validateApiVersion = (_, value) => {
    if (!value) return Promise.resolve()
    const versionRegex = /^\d{2}\.\d$/
    if (!versionRegex.test(value)) {
      return Promise.reject(new Error("API version must be in format XX.X (e.g., 58.0)"))
    }
    const version = Number.parseFloat(value)
    if (version < 20.0 || version > 60.0) {
      return Promise.reject(new Error("API version must be between 20.0 and 60.0"))
    }
    return Promise.resolve()
  }

  return (
    <div
      style={{
        position: "relative",
        minHeight: "100vh",
        padding: "48px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {/* Back button */}
      {onBack && (
        <Button
          type="text"
          onClick={onBack}
          style={{
            position: "absolute",
            top: "24px",
            left: "24px",
            color: "#6b7280", // Gray text
            fontSize: "16px",
            display: "flex",
            alignItems: "center",
            padding: "8px 12px",
            borderRadius: "8px",
          }}
        >
          <ArrowLeftOutlined style={{ marginRight: "8px" }} />
          Back
        </Button>
      )}

      <div
        style={{
          width: "100%",
          maxWidth: "800px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "32px",
            borderBottom: "1px solid #e5e7eb", // Light border
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Title level={2} style={{ margin: 0, color: "#1f2937", fontSize: "28px" }}>
              Create a Salesforce Source
            </Title>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                backgroundColor: "#f3f4f6", // Gray-100
                color: "#374151", // Gray-700
                padding: "6px 16px",
                borderRadius: "9999px", // Full rounded
                fontSize: "13px",
                fontWeight: "600",
                border: "1px solid #d1d5db", // Gray-300
                boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
              }}
            >
              <div
                style={{
                  width: "24px",
                  height: "24px",
                  backgroundColor: "#00a1e0", // Salesforce blue
                  borderRadius: "9999px", // Full rounded
                  marginRight: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  fontWeight: "bold",
                  fontSize: "14px",
                }}
              >
                <CloudOutlined />
              </div>
              Salesforce
            </div>
          </div>
          <Text style={{ color: "#4b5563", fontSize: "16px" }}>
            Connect your Salesforce account to seamlessly sync your data.
          </Text>
        </div>

        <div style={{ padding: "32px" }}>
          <Alert
            message={
              <span style={{ display: "flex", alignItems: "flex-start" }}>
                <InfoCircleOutlined style={{ marginRight: "12px", marginTop: "2px", color: "#2563eb" }} />
                <div>
                  <Text strong style={{ color: "#1e40af" }}>
                    Marketplace Connector
                  </Text>
                  <br />
                  <Text style={{ color: "#1e40af" }}>
                    Connectors are maintained by the Airbyte community. Airbyte does not offer support SLAs. Please
                    refer to our{" "}
                    <Link href="#" style={{ color: "#2563eb", textDecoration: "underline" }}>
                      documentation
                    </Link>{" "}
                    for more details.
                  </Text>
                </div>
              </span>
            }
            type="info"
            showIcon={false}
            style={{
              backgroundColor: "#eff6ff", // Blue-50
              border: "1px solid #bfdbfe", // Blue-200
              borderRadius: "8px",
              marginBottom: "32px",
              padding: "16px",
            }}
          />

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            onFinishFailed={handleSubmitFailed}
            autoComplete="off"
            initialValues={{ sourceName: "Salesforce", apiVersion: "58.0" }}
            style={{ display: "flex", flexDirection: "column", gap: "24px" }}
          >
            {/* Main form fields */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "24px" }}>
              <Form.Item
                label={
                  <Text style={{ color: "#374151", fontSize: "15px", fontWeight: "500" }}>
                    Source name <span style={{ color: "#ef4444" }}>*</span>{" "}
                    <Tooltip title="The name of your Salesforce source">
                      <InfoCircleOutlined style={{ color: "#9ca3af" }} />
                    </Tooltip>
                  </Text>
                }
                name="sourceName"
                rules={[
                  { required: true, message: "Source name is required" },
                  { min: 2, message: "Source name must be at least 2 characters" },
                  { max: 50, message: "Source name cannot exceed 50 characters" },
                ]}
                style={{ marginBottom: 0 }}
              >
                <Input
                  placeholder="e.g., My Salesforce Data"
                  style={{ height: "44px", fontSize: "15px", borderRadius: "6px" }}
                />
              </Form.Item>

              <Form.Item
                label={
                  <Text style={{ color: "#374151", fontSize: "15px", fontWeight: "500" }}>
                    Instance URL <span style={{ color: "#ef4444" }}>*</span>{" "}
                    <Tooltip title="The URL of your Salesforce instance (e.g., https://yourdomain.my.salesforce.com)">
                      <InfoCircleOutlined style={{ color: "#9ca3af" }} />
                    </Tooltip>
                  </Text>
                }
                name="instanceUrl"
                rules={[{ required: true, message: "Instance URL is required" }, { validator: validateInstanceUrl }]}
                style={{ marginBottom: 0 }}
              >
                <Input
                  placeholder="Enter instance URL"
                  style={{ height: "44px", fontSize: "15px", borderRadius: "6px" }}
                />
              </Form.Item>

              <Form.Item
                label={
                  <Text style={{ color: "#374151", fontSize: "15px", fontWeight: "500" }}>
                    Username <span style={{ color: "#ef4444" }}>*</span>{" "}
                    <Tooltip title="Your Salesforce username (email address)">
                      <InfoCircleOutlined style={{ color: "#9ca3af" }} />
                    </Tooltip>
                  </Text>
                }
                name="username"
                rules={[{ required: true, message: "Username is required" }, { validator: validateEmail }]}
                style={{ marginBottom: 0 }}
              >
                <Input
                  placeholder="Enter your username/email"
                  style={{ height: "44px", fontSize: "15px", borderRadius: "6px" }}
                  autoComplete="off"
                />
              </Form.Item>

              <Form.Item
                label={
                  <Text style={{ color: "#374151", fontSize: "15px", fontWeight: "500" }}>
                    Password <span style={{ color: "#ef4444" }}>*</span>{" "}
                    <Tooltip title="Your Salesforce password">
                      <InfoCircleOutlined style={{ color: "#9ca3af" }} />
                    </Tooltip>
                  </Text>
                }
                name="password"
                rules={[{ required: true, message: "Password is required" }]}
                style={{ marginBottom: 0 }}
              >
                <Input.Password
                  placeholder="Enter your password"
                  autoComplete="new-password"
                  iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                  style={{ height: "44px", fontSize: "15px", borderRadius: "6px" }}
                />
              </Form.Item>

              <Form.Item
                label={
                  <Text style={{ color: "#374151", fontSize: "15px", fontWeight: "500" }}>
                    Security Token <span style={{ color: "#ef4444" }}>*</span>{" "}
                    <Tooltip title="Your Salesforce security token">
                      <InfoCircleOutlined style={{ color: "#9ca3af" }} />
                    </Tooltip>
                  </Text>
                }
                name="securityToken"
                rules={[
                  { required: true, message: "Security token is required" },
                  { validator: validateSecurityToken },
                ]}
                style={{ marginBottom: 0 }}
              >
                <Input.Password
                  placeholder="Enter security token"
                  autoComplete="new-password"
                  iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                  style={{ height: "44px", fontSize: "15px", borderRadius: "6px" }}
                />
              </Form.Item>

              <Form.Item
                label={
                  <Text style={{ color: "#374151", fontSize: "15px", fontWeight: "500" }}>
                    API Version <span style={{ color: "#ef4444" }}>*</span>{" "}
                    <Tooltip title="The Salesforce API version to use (e.g., 58.0)">
                      <InfoCircleOutlined style={{ color: "#9ca3af" }} />
                    </Tooltip>
                  </Text>
                }
                name="apiVersion"
                rules={[{ required: true, message: "API Version is required" }, { validator: validateApiVersion }]}
                style={{ marginBottom: 0 }}
              >
                <Input placeholder="e.g., 58.0" style={{ height: "44px", fontSize: "15px", borderRadius: "6px" }} />
              </Form.Item>

              <Form.Item
                label={
                  <Text style={{ color: "#374151", fontSize: "15px", fontWeight: "500" }}>
                    Sandbox{" "}
                    <Tooltip title="Enable sandbox mode for testing with Salesforce's sandbox environment">
                      <InfoCircleOutlined style={{ color: "#9ca3af" }} />
                    </Tooltip>
                  </Text>
                }
                style={{ marginBottom: 0 }}
              >
                <Switch
                  checked={sandboxMode}
                  onChange={setSandboxMode}
                  style={{
                    backgroundColor: sandboxMode ? "#2563eb" : "#d1d5db", // Blue-600 or Gray-300
                  }}
                />
                <Text style={{ marginLeft: "12px", color: "#6b7280", fontSize: "14px" }}>
                  {sandboxMode ? "Sandbox mode enabled" : "Production mode"}
                </Text>
              </Form.Item>
            </div>

            {/* Action Buttons */}
            <Form.Item style={{ marginTop: "32px", marginBottom: 0, textAlign: "right" }}>
              <Space size="middle">
                <Button
                  onClick={() => form.resetFields()}
                  size="medium"
                  style={{
                    borderRadius: "6px",
                    fontSize: "16px",
                    padding: "0 24px",
                    borderColor: "#d1d5db", // Gray-300
                    color: "#374151", // Gray-700
                    boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                  }}
                >
                  Reset
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  size="medium"
                  style={{
                    backgroundColor: "#2563eb", // Blue-600
                    borderColor: "#2563eb",
                    borderRadius: "6px",
                    fontSize: "16px",
                    padding: "0 24px",
                    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                  }}
                >
                  {loading ? "Creating..." : "Create Source"}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </div>
      </div>
    </div>
  )
}
